import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface CorporateActionApplication {
  id: number;
  action_type: string;
  company_name: string;
  effective_date: string;
  broker_account_name: string;
  source_symbol?: string;
  source_isin: string;
  destination_isin: string;
  status: string;
  before_source_quantity: string;
  after_source_quantity: string;
  fractional_quantity: string;
  verification_details: Record<string, string>;
  error_message: string;
}

interface UserReport {
  id: number;
  application: number;
  status: string;
  message: string;
}

const STATUS_LABELS: Record<string, string> = {
  awaiting_statement_verification: 'Awaiting statement',
  applied_verified: 'Statement verified',
  conflict: 'Needs review',
  rolled_back: 'Rolled back',
  rollback_conflict: 'Rollback blocked',
};

const STATUS_COLORS: Record<string, string> = {
  awaiting_statement_verification: 'warning',
  applied_verified: 'success',
  conflict: 'danger',
  rolled_back: 'secondary',
  rollback_conflict: 'danger',
};

const CorporateActionHistory: React.FC = () => {
  const [applications, setApplications] = useState<CorporateActionApplication[]>([]);
  const [reports, setReports] = useState<UserReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<CorporateActionApplication | null>(null);
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const loadHistory = async () => {
    setLoading(true);
    setError('');
    try {
      const [historyResponse, reportsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/corporate-actions/history/`),
        axios.get(`${API_BASE_URL}/corporate-actions/reports/`),
      ]);
      setApplications(Array.isArray(historyResponse.data) ? historyResponse.data : []);
      setReports(Array.isArray(reportsResponse.data) ? reportsResponse.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to load corporate-action history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const submitReport = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || message.trim().length < 3) return;
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('application', String(selected.id));
      formData.append('message', message.trim());
      if (attachment) formData.append('attachment', attachment);
      await axios.post(`${API_BASE_URL}/corporate-actions/reports/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelected(null);
      setMessage('');
      setAttachment(null);
      await loadHistory();
    } catch (err: any) {
      setError(err?.response?.data?.message?.[0] || 'Unable to send the mismatch report.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="corporate-history-state"><Spinner animation="border" /><span>Loading action history...</span></div>;
  }

  return (
    <div className="corporate-history">
      <div className="corporate-history__intro">
        <div>
          <span className="page-eyebrow">Verified ledger changes</span>
          <h2>Corporate actions</h2>
          <p>Splits, bonus shares, and ISIN changes are kept separate from your real buy and sell history.</p>
        </div>
        <div className="corporate-history__truth">
          <strong>Your latest final statement wins</strong>
          <span>StockHunt compares every applied action with the next final statement for that exact account.</span>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {applications.length === 0 ? (
        <Card className="corporate-history__empty">
          <Card.Body>
            <strong>No corporate actions have changed your portfolio yet.</strong>
            <p>When one is approved and applies to your holdings, its calculation and statement check will appear here.</p>
          </Card.Body>
        </Card>
      ) : (
        <div className="corporate-history__grid">
          {applications.map((application) => {
            const report = reports.find((item) => item.application === application.id);
            const details = application.verification_details || {};
            return (
              <article className="corporate-history-card" key={application.id}>
                <div className="corporate-history-card__top">
                  <div>
                    <span>{application.action_type.replaceAll('_', ' ')}</span>
                    <h3>{application.source_symbol || application.company_name}</h3>
                    <small>{application.company_name}</small>
                  </div>
                  <Badge bg={STATUS_COLORS[application.status] || 'secondary'}>
                    {STATUS_LABELS[application.status] || application.status.replaceAll('_', ' ')}
                  </Badge>
                </div>
                <dl>
                  <div><dt>Account</dt><dd>{application.broker_account_name}</dd></div>
                  <div><dt>Effective</dt><dd>{new Date(`${application.effective_date}T00:00:00`).toLocaleDateString('en-IN')}</dd></div>
                  <div><dt>Before</dt><dd>{Number(application.before_source_quantity).toLocaleString('en-IN')}</dd></div>
                  <div><dt>After</dt><dd>{Number(application.after_source_quantity).toLocaleString('en-IN')}</dd></div>
                </dl>
                {details.snapshot_date && (
                  <div className={`corporate-history-card__check ${application.status === 'conflict' ? 'is-conflict' : ''}`}>
                    <span>Final statement {new Date(`${details.snapshot_date}T00:00:00`).toLocaleDateString('en-IN')}</span>
                    <strong>
                      Reported {Number(details.reported_source_quantity || 0).toLocaleString('en-IN')}
                      {' / '}Expected {Number(details.expected_source_quantity || 0).toLocaleString('en-IN')}
                    </strong>
                  </div>
                )}
                {Number(application.fractional_quantity) > 0 && (
                  <Alert variant="warning" className="py-2 mb-2">
                    Fractional entitlement {application.fractional_quantity} is still unresolved.
                  </Alert>
                )}
                {application.error_message && <p className="corporate-history-card__error">{application.error_message}</p>}
                <div className="corporate-history-card__footer">
                  {report ? (
                    <span>Mismatch report: <strong>{report.status}</strong></span>
                  ) : (
                    <Button variant="outline-danger" size="sm" onClick={() => setSelected(application)}>
                      Report a mismatch
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Modal show={Boolean(selected)} onHide={() => setSelected(null)} centered>
        <Form onSubmit={submitReport}>
          <Modal.Header closeButton>
            <Modal.Title>Report statement mismatch</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="light" className="border">
              Tell us what your final {selected?.broker_account_name} statement shows. An admin will review only your account result; the global action stays unchanged.
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label>What is different?</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Example: My July statement shows 19 shares, not 20."
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Statement page (optional)</Form.Label>
              <Form.Control type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAttachment(event.target.files?.[0] || null)} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setSelected(null)}>Cancel</Button>
            <Button type="submit" variant="danger" disabled={saving || message.trim().length < 3}>
              {saving ? <Spinner animation="border" size="sm" /> : 'Send for review'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default CorporateActionHistory;
